import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { Col, Form, Row } from 'react-bootstrap';
import { __MessageTypes } from '../../../common/Dictionaries';
import { isNullUndefined, isNullUndefinedOrEmpty, showMessage } from '../../../common/Helpers';
import MessageBoxInfoComplex from '../../../components/Common/Message/MessageBoxInfoComplex';
import PropTypes from 'prop-types';
import CTextAreaSimple from '../../../components/Common/CTextAreaSimple';
import { useOfGetEdPChecklistNotaList } from '../../../hooks/services/sPay/useOfEdPPrepare';
import { setCommentEdP } from '../../../services/sPay/edpConfig.service';
import moment from 'moment';
import { SpinnerSize } from '@fluentui/react';
import Loading from '../../../components/Templates/Site/Loading';

// Notas dinámicas del checklist "Back Office GST" exportado en PDF. Cada nota corresponde a un tipo
// del catálogo spay.edp_mensaje_tipo_checklist_pdf: se dibuja un control por cada elemento que traiga
// getNotasChecklistEP, sin cantidad fija. Cada nota se guarda como un EdpMensaje normal (mismo
// endpoint/handler que los comentarios de EdP), marcado con tipoChecklistReportePdfId.
const SidePanelViewEdPChecklistNotas = (props) => {
    const { t } = useTranslation();

    const {
        inputParam_ElementId = null,
        inputParam_EdPId = null,
        inputParam_IsPanelOpen = null,
        inputParam_SetterIsPanelOpen = null,
        inputParam_UserPropsKpi = null,
        // Uso opcional: cuando este panel se abre interceptando otra acción (ej. "Terminar revisión"
        // del AAD), inputParam_OnAfterSave se ejecuta después de guardar las notas (incluso si no hubo
        // ninguna nota nueva/modificada) para encadenar esa acción; inputParam_SaveButtonText permite
        // rotular el botón de guardado acorde a ese contexto.
        inputParam_OnAfterSave = null,
        inputParam_SaveButtonText = null,
    } = props;

    const canEdit = !isNullUndefined(inputParam_UserPropsKpi) ? !inputParam_UserPropsKpi.onlyObserver : false;

    const [messageConfig, setMessageConfig] = useState({
        message: null,
        messageType: "",
        showMessage: false,
    });

    const [listNotas, setListNotas] = useState(null);
    const [notas, setNotas] = useState([]);
    const [saveInProgress, setSaveInProgress] = useState(false);

    useOfGetEdPChecklistNotaList(inputParam_ElementId, inputParam_EdPId, listNotas, setListNotas);

    React.useEffect(() => {
        if (listNotas !== null) {
            setNotas(listNotas.map(nota => ({ ...nota })));
        }
    }, [listNotas]);

    const onDismissPanel = () => {
        showMessage(setMessageConfig, false, null, null)
        inputParam_SetterIsPanelOpen && inputParam_SetterIsPanelOpen(false)
    };

    const onClosePanel = () => {
        onDismissPanel();
    };

    const onNotaTextoChange = (e, tipoChecklistReportePdfId) => {
        const { value } = e.currentTarget;

        setNotas(prevNotas => prevNotas.map(nota =>
            nota.tipoChecklistReportePdfId === tipoChecklistReportePdfId
                ? { ...nota, textoComentario: value }
                : nota
        ));
    };

    const puedeEditarNota = (nota) => canEdit && (nota.mensajeId === 0 || nota.canEdit === true);

    const esNotaModificada = (nota) => {
        const original = listNotas?.find(x => x.tipoChecklistReportePdfId === nota.tipoChecklistReportePdfId);
        return (original?.textoComentario ?? '') !== (nota.textoComentario ?? '');
    };

    const onGuardarNotas = async () => {
        const notasAGuardar = notas.filter(nota => puedeEditarNota(nota) && !isNullUndefinedOrEmpty(nota.textoComentario) && esNotaModificada(nota));

        // En uso encadenado (inputParam_OnAfterSave) siempre se debe avanzar a la acción que sigue,
        // aunque el usuario no haya escrito ninguna nota nueva; en uso independiente, sin nada que
        // guardar no tiene sentido continuar.
        if (notasAGuardar.length === 0 && isNullUndefined(inputParam_OnAfterSave)) {
            showMessage(setMessageConfig, true, __MessageTypes.WARNING, t('data-lang-msg-requireddatanotset'));
            return;
        }

        setSaveInProgress(true);
        showMessage(setMessageConfig, false, null, null);

        try {
            for (const nota of notasAGuardar) {
                // Si la nota precargada no es de la iteración/etapa actual, se crea una nota nueva
                // (id = 0) en vez de sobrescribir el histórico de la iteración anterior.
                const objToSave = {
                    id: nota.esMismaIteracion ? nota.mensajeId : 0,
                    estadoPagoId: inputParam_EdPId,
                    estadoPagoPartidaId: null,
                    estadoPagoIncrementoId: null,
                    estadoPagoDeduccionId: null,
                    textoFijo: "",
                    textoComentario: nota.textoComentario,
                    // Fecha la estampa el backend con DateTime.Now (N_EdpMensajeHandler.Grabar); no se
                    // manda desde acá para no depender del reloj/zona horaria del cliente (antes se
                    // mandaba hora local con un "[Z]" que la rotulaba incorrectamente como UTC).
                    automatico: false,
                    rol: "",
                    userAuthProfileId: 0,
                    nombreApellido: "",
                    documentoUrl: "",
                    documentoLink: "",
                    documentId: 0,
                    tipoChecklistReportePdfId: nota.tipoChecklistReportePdfId,
                };

                const serviceResponse = await setCommentEdP(inputParam_ElementId, objToSave);

                if (!serviceResponse.data.succeeded || !serviceResponse.data.data.result) {
                    throw new Error('data-lang-msg-operationerror');
                }
            }

            if (!isNullUndefined(inputParam_OnAfterSave)) {
                await inputParam_OnAfterSave();
            }
            else {
                setListNotas(null);
                showMessage(setMessageConfig, true, __MessageTypes.SUCCESS, t('data-lang-msg-operationperformed'));
            }
        }
        catch (error) {
            showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'));
        }
        finally {
            setSaveInProgress(false);
        }
    };

    const onRenderFooterContent = () => {
        return <Row>
            <Col xl={12} className="message-verybottom-panel-medium">
                <MessageBoxInfoComplex
                    inputParam_Message={messageConfig.message}
                    inputParam_Display={messageConfig.showMessage}
                    inputParam_MessageType={messageConfig.messageType}
                    inputParam_SetterMessage={setMessageConfig}
                    inputParam_ShowClose={true}
                />
            </Col>
            <Col xl={12} className='d-flex'>
                {
                    canEdit && !saveInProgress &&
                    <PrimaryButton className='mx-2' onClick={onGuardarNotas}>{!isNullUndefinedOrEmpty(inputParam_SaveButtonText) ? inputParam_SaveButtonText : t('data-lang-btn-save')}</PrimaryButton>
                }
                {
                    saveInProgress &&
                    <Loading
                        inputParam_Style={{ marginTop: 0 }}
                        inputParam_ShowLoading={true}
                        inputParam_ShowText={true}
                        inputParam_Text={t('data-lang-lbl-executing')}
                        inputParam_Position="unset"
                        inputParam_Width="20px"
                        inputParam_Height="20px"
                        inputParam_Vertical={false}
                        inputParam_ColClassName="my-auto"
                        inputParam_EjecutingTextClass={"ps-4"}
                        inputParam_Left="87%"
                        inputParam_Top="11px"
                        inputParam_RootClass="pe-4 loading-buttonSize-font-size"
                    />
                }
                <DefaultButton onClick={onClosePanel}>{t('data-lang-btn-close')}</DefaultButton>
            </Col>
        </Row>
    }

    return (
        <div>
            <Panel
                isOpen={inputParam_IsPanelOpen}
                isBlocking={true}
                onDismiss={onDismissPanel}
                headerText={'Notas checklist Back Office GST'}
                closeButtonAriaLabel={t('data-lang-btn-close')}
                isFooterAtBottom={true}
                onRenderFooterContent={onRenderFooterContent}
                customWidth={PanelType.large}
                type={PanelType.large}
            >
                <Row>
                    <Col xs={12}>
                        {
                            isNullUndefined(listNotas) &&
                            <Loading inputParam_ShowLoading={true} inputParam_ShowText={false} inputParam_SpinnerSize={SpinnerSize.medium} />
                        }
                        {
                            !isNullUndefined(listNotas) && listNotas.length === 0 &&
                            <Row className='pt-3'><Col>{t('data-lang-msg-nodata')}</Col></Row>
                        }
                        {
                            notas.map((nota) => {
                                const notaEditable = puedeEditarNota(nota);

                                return (
                                    <Row className='pt-3' key={nota.tipoChecklistReportePdfId}>
                                        <Col>
                                            {
                                                notaEditable &&
                                                <CTextAreaSimple
                                                    editMode={true}
                                                    rows={4}
                                                    labelText={nota.tipoNombre}
                                                    controlValue={nota.textoComentario}
                                                    setterControlIsDynamic={true}
                                                    setterField={nota.tipoChecklistReportePdfId}
                                                    setterControlValue={onNotaTextoChange}
                                                />
                                            }
                                            {
                                                !notaEditable &&
                                                <>
                                                    <b><Form.Label className="pe-2">{nota.tipoNombre}:</Form.Label></b>
                                                    <div style={{ backgroundColor: '#f3f2f1' }} className="body-content p-2">
                                                        {!isNullUndefinedOrEmpty(nota.textoComentario) ? nota.textoComentario : '-'}
                                                    </div>
                                                </>
                                            }
                                            {
                                                !isNullUndefinedOrEmpty(nota.nombreApellido) &&
                                                <div className="text-muted" style={{ fontSize: '0.8em' }}>
                                                    {nota.nombreApellido}{!isNullUndefinedOrEmpty(nota.rol) ? ` (${nota.rol})` : ''} - {moment(nota.fecha).format('DD/MM/YYYY')}
                                                </div>
                                            }
                                        </Col>
                                    </Row>
                                );
                            })
                        }
                    </Col>
                </Row>
            </Panel>
        </div>
    );
};

SidePanelViewEdPChecklistNotas.propTypes = {
    inputParam_IsPanelOpen: PropTypes.bool.isRequired,
    inputParam_SetterIsPanelOpen: PropTypes.func.isRequired,
};

export default SidePanelViewEdPChecklistNotas;
